/* Gestione profilo (port di profile.js) — dati account selezionato, livelli, preferenze. */
import { useEffect, useState } from "react";
import { ptapp } from "@/lib/ptapp";
import { Avatar, Card, Row, AccountSelect } from "./_ui";

const SPORTS = [{ id: "PADEL", label: "Padel" }, { id: "PICKLEBALL", label: "Pickleball" }, { id: "TENNIS", label: "Tennis" }];
const genderLabel = (g?: string) => { const u = String(g || "").toUpperCase(); return u === "MALE" ? "Uomo" : u === "FEMALE" ? "Donna" : (g || "—"); };
const pick = (o: any, keys: string[]) => { if (!o) return undefined; for (const k of keys) if (o[k] != null && o[k] !== "") return o[k]; return undefined; };
const fmtLevel = (v: any) => v == null ? "—" : (Number.isFinite(+v) ? (+v).toFixed(2).replace(".", ",") : String(v));
const HAND: Record<string, string> = { RIGHT: "Destra", LEFT: "Sinistra" };
const SIDE: Record<string, string> = { BACKHAND: "Rovescio", FOREHAND: "Dritto" };
const MTYPE: Record<string, string> = { FRIENDLY: "Amichevole", COMPETITIVE: "Competitiva" };
const mapList = (a: string[] | undefined, m: Record<string, string>) => (a || []).map((x) => m[x] || x).join(", ");

export default function Profile() {
  const [acct, setAcct] = useState("moving");
  const [me, setMe] = useState<any>(null);
  const [uid, setUid] = useState("");
  const [levels, setLevels] = useState<Record<string, any>>({});
  const [prefs, setPrefs] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true; setLoading(true); setMe(null); setLevels({}); setPrefs({});
    const get = <X,>(fn: () => Promise<X>) => fn().then((r) => r).catch(() => null);
    (async () => {
      const u = await ptapp.uid(acct).catch(() => ({ uid: "" }));
      if (!alive) return; setUid(String(u.uid));
      const [appMe, apiMe] = await Promise.all([
        get(() => ptapp.call<any>(acct, "app", "v2/users/me")),
        get(() => ptapp.call<any>(acct, "api", "v2/users/me")),
      ]);
      if (!alive) return;
      setMe(Object.assign({}, apiMe || {}, appMe || {}));
      const lv: Record<string, any> = {}, pf: Record<string, any> = {};
      await Promise.all(SPORTS.flatMap((s) => [
        get(() => ptapp.call<any>(acct, "api", "v1/levels", { params: { user_id: u.uid, sport_id: s.id } })).then((d) => {
          const list = Array.isArray(d) ? d : (d ? [d] : []); const e = list.find((x: any) => x && (x.sport_id === s.id)) || list[0];
          if (e) lv[s.id] = pick(e, ["level_value", "level", "value", "rating"]);
        }),
        get(() => ptapp.call<any>(acct, "app", "v1/profiles/" + u.uid + "/sport_preferences/" + s.id)).then((d) => { if (d) pf[s.id] = d; }),
      ]));
      if (!alive) return; setLevels({ ...lv }); setPrefs({ ...pf }); setLoading(false);
    })();
    return () => { alive = false; };
  }, [acct]);

  const name = pick(me, ["full_name", "name", "display_name"]) || [pick(me, ["first_name"]), pick(me, ["last_name"])].filter(Boolean).join(" ") || ("utente " + uid);
  const email = pick(me, ["email", "email_address"]) || "—";
  const pic = pick(me, ["picture", "avatar", "image"]);
  const phone = pick(me, ["phone", "phone_number", "full_phone"]);
  const lang = pick(me, ["locale", "language", "preferred_language"]) || "—";

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-4">
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-bold text-slate-900">Gestione profilo</h2>
        <AccountSelect value={acct} onChange={setAcct} />
      </div>

      <Card title="Il mio profilo">
        {loading && !me ? <div className="text-sm text-slate-500">carico…</div> : (
          <div>
            <div className="flex items-center gap-3">
              <Avatar pic={pic} size={64} />
              <div className="min-w-0"><div className="text-lg font-semibold text-slate-900">{name}</div><div className="text-sm text-slate-500">{email}</div></div>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-x-8 sm:grid-cols-3">
              <Row k="Genere" v={genderLabel(pick(me, ["gender", "sex"]))} />
              <Row k="Telefono" v={phone || "—"} />
              <Row k="Lingua" v={lang} />
              <Row k="uid" v={uid} />
            </div>
          </div>
        )}
      </Card>

      <Card title="Livelli per sport">
        {SPORTS.map((s) => <Row key={s.id} k={s.label} v={<span className="rounded-full bg-slate-100 px-2 text-slate-700">{fmtLevel(levels[s.id])}</span>} />)}
      </Card>

      <Card title="Preferenze di gioco">
        {SPORTS.map((s) => {
          const p = prefs[s.id]; if (!p) return <div key={s.id} className="mt-2"><div className="text-sm font-semibold text-slate-700">{s.label}</div><div className="text-xs text-slate-400">nessuna preferenza</div></div>;
          return (
            <div key={s.id} className="mt-2">
              <div className="text-sm font-semibold text-slate-700">{s.label}</div>
              <Row k="Mano" v={mapList(p.best_hand, HAND)} />
              <Row k="Posizione" v={p.court_side && p.court_side.length === 2 ? "Entrambi i lati" : mapList(p.court_side, SIDE)} />
              <Row k="Tipo partita" v={mapList(p.type_of_match, MTYPE)} />
            </div>
          );
        })}
      </Card>
    </div>
  );
}
